
import { Image, Box, Center, useBreakpointValue } from '@telescope/cassini-ui';
import { useAnimate } from 'framer-motion';
import { useState, useCallback, useEffect } from 'react';
import { AnimationOptions, SLOT_SIZE } from 'features';

type ReelProps = {
  items: any[]
  offset?: number
  target: number
  isAnimating?: boolean
  onComplete?: null | (() => void)
  options?: {
    animation: AnimationOptions
  }
  styles?: Record<string, any>
}

export const Reel = ({ 
  items, 
  offset = 0, 
  target, 
  isAnimating, 
  onComplete, 
  options = { 
    animation: {} 
  },
  styles = {}
} : ReelProps) => {
  const [scope, animate] = useAnimate();

  const slotSize = useBreakpointValue(SLOT_SIZE)!;

  const NUMBER_OF_TURNS = 3;
  const SLOTS_PER_REEL = items.length;
  const DEGREES = 360 / SLOTS_PER_REEL;
  const PADDING = 0.8;

  const radius = Math.round((slotSize.height / 2) / Math.tan(Math.PI / SLOTS_PER_REEL)) + (PADDING * SLOTS_PER_REEL); 
  const numberOfTurns = NUMBER_OF_TURNS + offset;

  const initialPosition = -((numberOfTurns * 360) + (target * DEGREES))
  const [ degree, setDegree ] = useState(initialPosition);
  const [ prevTarget, setPrevTarget ] = useState(target);

  const getRotationValue = useCallback((target: number) => {
    let targetOffset = target - prevTarget;
    return -((numberOfTurns * 360) + (targetOffset * DEGREES) - degree)
  }, [numberOfTurns, degree, prevTarget])

  useEffect(() => {
    if (!isAnimating) return;

    const value = getRotationValue(target);
    setDegree(value);
    setPrevTarget(target);

    const animation = async () => {
      await animate(scope.current, { 
        rotateX: value 
      }, { 
        duration: numberOfTurns, 
        ease: 'easeOut',
        ...options.animation,
        onComplete: () => {
          if (onComplete) onComplete()
        },
      });
    }

    animation();
  }, [isAnimating, target]);

  return (
    <Center bg="primary.500" border="1px solid #fff" px={3}
      sx={{ '*': { transformStyle: 'preserve-3d' }, ...styles }}
      h={slotSize.height * 2.5}
      position="relative"
      overflow="hidden">
      <Box
          h={`${slotSize.height}px`}
          w={`${slotSize.width}px`}
          sx={{ 
            perspective: `${SLOTS_PER_REEL * 1000}px` 
          }} 
          position="relative">
        <Box 
          transform={`rotateX(${degree}deg)`}
          ref={scope}
          position="absolute"
          w="100%"
          h="100%">
            { items.map((item, i) => {
              return (
                <Box key={i} 
                  position="absolute" w="100%" h="100%"
                  transform={`rotateX(${DEGREES * i}deg) translateZ(${radius}px)`}
                  transformOrigin="50% 50%"
                  sx={{ backfaceVisibility: "hidden" }}>
                    <Image src={item.image} alt={item.description} position="absolute" w="100%" h="100%"/>
                </Box>
              )
            }) }
        </Box>

      </Box>
      <ReelOverlay />
    </Center>
  )
}

const ReelOverlay = () => {
  return (
    <Box position="absolute" w="100%" h="100%">
      <Box 
        w="100%" h="100%" position="absolute"
        bg="linear-gradient(180deg, rgba(0,0,0,.6) 0%, rgba(0,0,0,0) 5%, rgba(0,0,0,0) 40%, rgba(0,0,0,0) 60%, rgba(0,0,0,0) 95%, rgba(0,0,0,.6) 100%)" />
      <Box 
        w="100%" h="100%" position="absolute"
        bg="linear-gradient(90deg, rgba(0,0,0,.3) 0%, rgba(0,0,0,0) 5%, rgba(0,0,0,0) 40%, rgba(0,0,0,0) 60%, rgba(0,0,0,0) 95%, rgba(0,0,0,.3) 100%)" />

      <Box w="7px" h="2px" bg="#fff" top="50%" left={0} position="absolute" />
      <Box w="7px" h="2px" bg="#fff" top="50%" right={0} position="absolute" />
    </Box>
  )
}